/** Typed API client for the `/gis-queue` router (GIS Phase 2A, Tasks 4-9).
 *  Backend router: src/topology_platform/api/v1/gis_queue.py.
 *
 *  One function per endpoint; every request/response shape comes from
 *  `./types.ts`, which mirrors `schemas/gis_queue.py` field-for-field. */

import { get, post } from '../../api/client.ts'
import type {
  BulkExecuteRequest,
  BulkExecuteResponse,
  BulkPreviewRequest,
  BulkPreviewResponse,
  ChangeRequestDetail,
  ChangeRequestListResponse,
  ChangeRequestWithdrawResponse,
  ChangeRequestsQuery,
  EvidenceResponse,
  FindingActionRequest,
  FindingActionResponse,
  FindingsListResponse,
  FindingsQuery,
  WithdrawChangeRequestRequest,
} from './types.ts'

function buildQS(params: object): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params) as [string, string | number | boolean | undefined | null][]) {
    if (v !== undefined && v !== null) qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

// =============================================================================
// Findings (Task 4)
// =============================================================================

/** `GET /gis-queue/findings` — filtered, paginated, with per-type counts.
 *  Gated `gis.queue.view` server-side. */
export function getFindings(query: FindingsQuery = {}): Promise<FindingsListResponse> {
  return get<FindingsListResponse>(`/gis-queue/findings${buildQS(query)}`)
}

// =============================================================================
// Evidence (Task 5). NEVER call this for a `new_in_gis` finding — the
// endpoint 422s on it by design (spec §5); FindingRow's expand logic must not
// reach this call for that type.
// =============================================================================

export function getFindingEvidence(findingId: number): Promise<EvidenceResponse> {
  return get<EvidenceResponse>(`/gis-queue/findings/${findingId}/evidence`)
}

// =============================================================================
// Single-row triage action (Task 6/7a/7b)
// =============================================================================

/** `POST /gis-queue/findings/{id}/action`. Gated `gis.triage`; entity-mutating
 *  actions additionally need `gis.override` (see `ACTION_SPECS` in
 *  `./types.ts`). Only send `target_parent_id` for assign_parent/
 *  propose_parent — the backend silently ignores it on every other action
 *  (a documented footgun; callers should not rely on that and should omit it
 *  themselves). */
export function actOnFinding(
  findingId: number,
  body: FindingActionRequest,
): Promise<FindingActionResponse> {
  return post<FindingActionResponse>(`/gis-queue/findings/${findingId}/action`, body)
}

// =============================================================================
// Bulk triage (Task 8)
// =============================================================================

/** `POST /gis-queue/bulk/preview`. Gated `gis.triage.bulk` + `gis.queue.view`. */
export function previewBulkTriage(body: BulkPreviewRequest): Promise<BulkPreviewResponse> {
  return post<BulkPreviewResponse>('/gis-queue/bulk/preview', body)
}

/** `POST /gis-queue/bulk/execute`. Gated `gis.triage.bulk`. `filter_hash` must
 *  be the value a prior `previewBulkTriage` call for this EXACT
 *  {filter, action, note} returned — any drift 409s. */
export function executeBulkTriage(body: BulkExecuteRequest): Promise<BulkExecuteResponse> {
  return post<BulkExecuteResponse>('/gis-queue/bulk/execute', body)
}

// =============================================================================
// Change requests (Task 9)
// =============================================================================

/** `GET /gis-queue/crs`. Gated `gis.queue.view`. */
export function getChangeRequests(
  query: ChangeRequestsQuery = {},
): Promise<ChangeRequestListResponse> {
  return get<ChangeRequestListResponse>(`/gis-queue/crs${buildQS(query)}`)
}

/** `GET /gis-queue/crs/{id}`. Gated `gis.queue.view`. */
export function getChangeRequest(crId: number): Promise<ChangeRequestDetail> {
  return get<ChangeRequestDetail>(`/gis-queue/crs/${crId}`)
}

/** `POST /gis-queue/crs/{id}/withdraw`. Gated `gis.cr.withdraw` ALONE (not
 *  composed with `gis.queue.view`) — restores the effective value per the 1C
 *  contract (except a queue-minted relink, which the backend exempts). */
export function withdrawChangeRequest(
  crId: number,
  body: WithdrawChangeRequestRequest = {},
): Promise<ChangeRequestWithdrawResponse> {
  return post<ChangeRequestWithdrawResponse>(`/gis-queue/crs/${crId}/withdraw`, body)
}
